import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { UserAuthData } from 'src/app/models/auth';
import { ApplicationEventsEnum } from 'src/app/models/events';
import { ModulesEnum } from 'src/app/models/modules';
import { AuthService } from 'src/app/services/auth.service';
import { EventsService } from 'src/app/services/events.service';

@Component({
  selector: 'app-iframe',
  templateUrl: './iframe.component.html',
  styleUrls: ['./iframe.component.scss'],
})
export class IframeComponent implements OnInit, OnDestroy {
  @ViewChild('child') childFrame: ElementRef<HTMLIFrameElement> | null = null;
  sanitizedUrl: SafeUrl | null = null;
  currentRoute: ModulesEnum = ModulesEnum.Auth;
  userAuthData: UserAuthData | null = null;
  private ngUnsubscribe = new Subject<void>();

  constructor(
    private eventsService: EventsService,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    combineLatest([this.route.params, this.route.data])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([params, data]) => {
        const { route } = data;
        const targetUrl = this.urlBuilder(params, data);
        this.sanitizedUrl =
          this.sanitizer.bypassSecurityTrustResourceUrl(targetUrl);
        this.currentRoute = route;
      });

    this.authService.authUserData$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((authUserData) => {
        this.userAuthData = authUserData;
      });

    this.eventsService.setAuthData$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        if (this.childFrame?.nativeElement) {
          this.eventsService.notifyChild(
          this.childFrame.nativeElement,
          {
            type: ApplicationEventsEnum.UserAuthData,
            message: this.userAuthData ?? {} as UserAuthData,
            source: window.location.origin,
          },
          this.childFrame.nativeElement.src,
        );
      }});
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  iframeLoaded(element: HTMLIFrameElement): void {
    if (element?.src) {
      this.actOnRoute(element, this.currentRoute);
    }
  }

  actOnRoute(element: HTMLIFrameElement, route: ModulesEnum): void {
    this.eventsService.notifyChild(
      element,
      {
        type: ApplicationEventsEnum.OriginSiteUrl,
        message: window.location.origin,
        source: window.location.origin,
      },
      element.src,
    );
    if (this.eventsService.childUrl) {
      this.eventsService.notifyChild(
        element,
        {
          type: ApplicationEventsEnum.RouteChanged,
          message: this.eventsService.childUrl,
          source: window.location.origin,
        },
        element.src,
      );
    }
    if (this.userAuthData) {
      this.eventsService.notifyChild(
        element,
        {
          type: ApplicationEventsEnum.UserAuthData,
          message: this.userAuthData,
          source: window.location.origin,
        },
        element.src,
      );
    }
  }

  urlBuilder(params: any, data: any): string {
    switch (data.route) {
      case 'financials':
        if (params.category === 'approvals') {
          return `${data.url}${params.category}/${params.id}${
            params.section ? '/' + params.section : ''
          }${params.sectionId ? '/' + params.sectionId : ''}`;
        }
        if (params.category === 'ledger-details') {
          return `${data.url}ledgers/${params.category}/${params.id}`;
        }
        break;
      case 'zones': {
        if (params.category === 'bulk') {
          if (params.id) {
            if (params.section) {
              return `${data.url}/zones/${params.category}/${params.id}/${params.section}`;
            }
          }
        }

        if (params.category === 'bulk' && params.id) {
          return `${data.url}/zones/${params.category}/${params.id}`;
        }
        break;
      }
    }
    if (params?.id) {
      return `${data.url}${params.id}`;
    } else {
      return `${data.url}`;
    }
  }
}
